import React, { useEffect, useState, useRef } from "react";
import { renderError } from "../../../helpers/errors";
import { createFilterQueryString } from "../../../helpers/helpers";
import { convertIntToMoney } from "../../../helpers/conversions";
import api from "../../../services/api";
import InputSelect from "../../InputSelect";

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_RESULTS_PER_PAGE = 20;
const MIN_SEARCH = 1;

const toOption = order => ({
    value: order.id,
    label: [
        order.code,
        order.customer_company_trade_name ?? order.customer_name,
        convertIntToMoney(order.total ?? 0, true),
    ].filter(Boolean).join(' - '),
});

// Busca de pedidos por código, nome da pessoa ou nome da empresa (grupos de
// filtro em OR, sobre as colunas denormalizadas do pedido). Usa /order/paginate,
// que já devolve o shape enxuto da listagem.
export const SelectOrder = ({
    label = 'Pedido',
    selectedOrder,
    setSelectedOrder,
    perPage = DEFAULT_RESULTS_PER_PAGE,
}) => {
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [search, setSearch] = useState('');
    const [noOptionsText, setNoOptionsText] = useState(null);
    const debounceRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (search.length < MIN_SEARCH) {
            setNoOptionsText(`Digite o código, a pessoa ou a empresa...`);
            setOptions([]);
            if (abortRef.current) abortRef.current.abort();
            return;
        }
        debounceRef.current = setTimeout(() => getOrders(), SEARCH_DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [search]);

    const getOrders = () => {
        setOptions([]);
        setNoOptionsText('Carregando...');
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        const stringFilters = createFilterQueryString([
            [`[code][equal]=${search}`],
            [`[customer_name][contain]=${search}`],
            [`[customer_company_trade_name][contain]=${search}`],
        ]);

        api.get(`/order/paginate?${stringFilters}per_page=${perPage}`, {
            signal: abortRef.current.signal,
        }).then(res => {
            const raw = res.data?.resources?.data ?? res.data?.resources ?? [];
            const list = Array.isArray(raw) ? raw : [];
            setOptions(list.map(toOption));
            setNoOptionsText(list.length === 0 ? 'Nenhum pedido encontrado' : null);
        }).catch(error => {
            if (error.code === 'ERR_CANCELED') return;
            renderError(error);
            setNoOptionsText('Erro ao carregar. Tente novamente.');
        });
    }

    const handleChange = value => {
        setSelectedOption(options.find(option => option.value === value) ?? null);
        setSelectedOrder(value);
    }

    // Mantém a opção escolhida visível mesmo quando uma nova busca troca a lista.
    const visibleOptions = selectedOption && !options.some(option => option.value === selectedOption.value)
        ? [selectedOption, ...options]
        : options;

    return (
        <InputSelect
            noOptionsText={noOptionsText}
            label={label}
            options={visibleOptions}
            value={selectedOrder}
            change={handleChange}
            setSearch={value => setSearch(value)}
            filterOption={true}
        />
    );
}

export default SelectOrder;
